import React, { useState, useEffect } from 'react';
import { staffApi } from '../../staffApi';

function CreateChatRoom({ isOpen, onClose, onChatRoomCreated }) {
    const [formData, setFormData] = useState({
        title: '',
        studentId: '',
        tutorId: ''
    });
    const [students, setStudents] = useState([]);
    const [tutors, setTutors] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [filteredTutors, setFilteredTutors] = useState([]);
    const [studentSearch, setStudentSearch] = useState('');
    const [tutorSearch, setTutorSearch] = useState('');
    const [showStudentDropdown, setShowStudentDropdown] = useState(false);
    const [showTutorDropdown, setShowTutorDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Fetch students and tutors when component opens
    useEffect(() => {
        if (isOpen) {
            fetchUsers();
        }
    }, [isOpen]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-container')) {
                setShowStudentDropdown(false);
                setShowTutorDropdown(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            // Fetch students
            const studentsResponse = await staffApi.getStudents();
            console.log('Students response:', studentsResponse);
            if (studentsResponse.success) {
                const studentsList = studentsResponse.data || [];
                console.log('Students list:', studentsList);
                setStudents(studentsList);
                setFilteredStudents(studentsList);
            }

            // Fetch tutors
            const tutorsResponse = await staffApi.getTutors();
            console.log('Tutors response:', tutorsResponse);
            if (tutorsResponse.success) {
                const tutorsList = tutorsResponse.data.tutors || [];
                console.log('Tutors list:', tutorsList);
                setTutors(tutorsList);
                setFilteredTutors(tutorsList);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users');
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    // Student search and selection handlers
    const handleStudentSearch = (e) => {
        const searchTerm = e.target.value.toLowerCase();
        setStudentSearch(searchTerm);
        
        if (Array.isArray(students)) {
            const filtered = students.filter(student => 
                student.name.toLowerCase().includes(searchTerm) ||
                student.email.toLowerCase().includes(searchTerm)
            );
            setFilteredStudents(filtered);
        } else {
            console.warn('Students is not an array:', students);
            setFilteredStudents([]);
        }
        setShowStudentDropdown(true);
    };

    const handleStudentSelect = (student) => {
        setFormData(prev => ({
            ...prev,
            studentId: student.id
        }));
        setStudentSearch(student.name);
        setShowStudentDropdown(false);
    };

    const clearStudentSelection = () => {
        setFormData(prev => ({
            ...prev,
            studentId: ''
        }));
        setStudentSearch('');
    };

    // Tutor search and selection handlers
    const handleTutorSearch = (e) => {
        const searchTerm = e.target.value.toLowerCase();
        setTutorSearch(searchTerm);
        
        if (Array.isArray(tutors)) {
            const filtered = tutors.filter(tutor => 
                tutor.name.toLowerCase().includes(searchTerm) ||
                tutor.email.toLowerCase().includes(searchTerm)
            );
            setFilteredTutors(filtered);
        } else {
            console.warn('Tutors is not an array:', tutors);
            setFilteredTutors([]);
        }
        setShowTutorDropdown(true);
    };

    const handleTutorSelect = (tutor) => {
        setFormData(prev => ({
            ...prev,
            tutorId: tutor.id
        }));
        setTutorSearch(tutor.name);
        setShowTutorDropdown(false);
    };

    const clearTutorSelection = () => {
        setFormData(prev => ({
            ...prev,
            tutorId: ''
        }));
        setTutorSearch('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            setError('Please enter a chat room title');
            return;
        }

        if (!formData.studentId && !formData.tutorId) {
            setError('Please select at least one student or tutor');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Prepare participants array
            const participants = [];
            
            // Add student if selected
            if (formData.studentId) {
                participants.push({
                    user_id: formData.studentId,
                    role: 'student'
                });
            }

            // Add tutor if selected
            if (formData.tutorId) {
                participants.push({
                    user_id: formData.tutorId,
                    role: 'tutor'
                });
            }

            // Determine chat type based on participants
            let chatType = 'general';
            if (formData.studentId && formData.tutorId) {
                chatType = 'staff_student_tutor';
            } else if (formData.studentId) {
                chatType = 'staff_student';
            } else if (formData.tutorId) {
                chatType = 'staff_tutor';
            }

            // Create chat room data
            const chatRoomData = {
                title: formData.title.trim(),
                description: `Chat room created by staff for ${formData.studentId ? 'student' : ''}${formData.studentId && formData.tutorId ? ' and ' : ''}${formData.tutorId ? 'tutor' : ''}`,
                chat_type: chatType,
                participants: participants
            };

            await staffApi.createChatRoom(chatRoomData);

            // Reset form
            setFormData({
                title: '',
                studentId: '',
                tutorId: ''
            });

            // Notify parent component
            onChatRoomCreated();
            onClose();

        } catch (err) {
            console.error('Error creating chat room:', err);
            setError(err.response?.data?.message || 'Failed to create chat room');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            title: '',
            studentId: '',
            tutorId: ''
        });
        setStudentSearch('');
        setTutorSearch('');
        setShowStudentDropdown(false);
        setShowTutorDropdown(false);
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-800">Create Chat Room</h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Chat Room Title */}
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                            Chat Room Title *
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="Enter chat room title..."
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                            required
                        />
                    </div>

                    {/* Student Selection */}
                    <div className="relative dropdown-container">
                        <label htmlFor="studentSearch" className="block text-sm font-medium text-gray-700 mb-2">
                            Add Student (Optional)
                        </label>
                        {loadingUsers ? (
                            <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-500">
                                Loading students...
                            </div>
                        ) : (
                            <>
                                <div className="relative">
                                    <input
                                        type="text"
                                        id="studentSearch"
                                        value={studentSearch}
                                        onChange={handleStudentSearch}
                                        onFocus={() => setShowStudentDropdown(true)}
                                        placeholder="Search students by name or email..."
                                        className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    />
                                    {formData.studentId && (
                                        <button
                                            type="button"
                                            onClick={clearStudentSelection}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                {showStudentDropdown && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                                        {filteredStudents.length > 0 ? (
                                            filteredStudents.map(student => (
                                                <div
                                                    key={student.id}
                                                    onClick={() => handleStudentSelect(student)}
                                                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                >
                                                    <div className="font-medium text-gray-900">{student.name}</div>
                                                    <div className="text-sm text-gray-500">{student.email}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-4 py-3 text-gray-500 text-center">
                                                No students found
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Tutor Selection */}
                    <div className="relative dropdown-container">
                        <label htmlFor="tutorSearch" className="block text-sm font-medium text-gray-700 mb-2">
                            Add Tutor (Optional)
                        </label>
                        {loadingUsers ? (
                            <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-500">
                                Loading tutors...
                            </div>
                        ) : (
                            <>
                                <div className="relative">
                                    <input
                                        type="text"
                                        id="tutorSearch"
                                        value={tutorSearch}
                                        onChange={handleTutorSearch}
                                        onFocus={() => setShowTutorDropdown(true)}
                                        placeholder="Search tutors by name or email..."
                                        className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    />
                                    {formData.tutorId && (
                                        <button
                                            type="button"
                                            onClick={clearTutorSelection}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                {showTutorDropdown && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                                        {filteredTutors.length > 0 ? (
                                            filteredTutors.map(tutor => (
                                                <div
                                                    key={tutor.id}
                                                    onClick={() => handleTutorSelect(tutor)}
                                                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                >
                                                    <div className="font-medium text-gray-900">{tutor.name}</div>
                                                    <div className="text-sm text-gray-500">{tutor.email}</div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-4 py-3 text-gray-500 text-center">
                                                No tutors found
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
                        >
                            {loading ? 'Creating...' : 'Create Chat Room'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CreateChatRoom;
